/**
 * components/BetaApplication/steps/TradingSetupStep.tsx
 * Trading Setup Step - Step 2A of Beta Application
 *
 * Collects trading setup information:
 * - Primary trading focus (asset class)
 * - Account type with conditional prop firm details
 * - Broker information
 */

// 1. External dependencies
import React, { useState, useMemo, useEffect, useCallback } from 'react';

// 2. Internal absolute imports
import { Form, Input, Select, SelectItem, Card, CardHeader, CardBody } from '@enfomentis/ui';
import { useBetaApplicationData } from '../hooks/useBetaApplicationData';
import {
  validateTradingStep,
  validateRequiredSelection,
  validateRequiredMultiSelection,
  validateBroker
} from '../utils/betaApplicationValidation';
import {
  TradingFocusEnum,
  BetaAccountTypeEnum,
  PropFirmStatusEnum,
  PropFirmPayoutHistoryEnum,
  type TradingFocus,
  type BetaAccountType,
  type PropFirmStatus,
  type PropFirmPayoutHistory
} from '../types/BetaApplicationTypes';

/**
 * Trading Setup Step Component
 *
 * Complete functional form for collecting trading setup information
 * with conditional prop firm logic and real-time validation.
 */
export const TradingSetupStep: React.FC = () => {
  const { data: draft, updateData } = useBetaApplicationData();

  // Field touched states for validation timing
  const [primaryFocusTouched, setPrimaryFocusTouched] = useState(false);
  const [accountTypeTouched, setAccountTypeTouched] = useState(false);
  const [propFirmTouched, setPropFirmTouched] = useState(false);
  const [propFirmStatusTouched, setPropFirmStatusTouched] = useState(false);
  const [propFirmPayoutHistoryTouched, setPropFirmPayoutHistoryTouched] = useState(false);
  const [brokerTouched, setBrokerTouched] = useState(false);

  // Local validation functions for HeroUI components
  const validatePrimaryFocusField = (values: string[]): string | null => {
    const result = validateRequiredMultiSelection(values, 'Primary asset(s) traded');
    return result.isValid ? null : result.error || null;
  };

  const validateAccountTypeField = (value: string): string | null => {
    const result = validateRequiredSelection(value, 'Account type');
    return result.isValid ? null : result.error || null;
  };

  const validatePropFirmField = useCallback((value: string): string | null => {
    if (!draft) return null;
    if (draft.trading.accountType !== 'prop-firm') return null;
    if (!value.trim()) return 'Prop firm name is required';
    if (value.length > 100) return 'Prop firm name is too long';
    return null;
  }, [draft]);

  const validatePropFirmStatusField = useCallback((value: string): string | null => {
    if (!draft) return null;
    if (draft.trading.accountType !== 'prop-firm') return null;
    if (!value) return 'Prop firm status is required';
    return null;
  }, [draft]);

  const validatePropFirmPayoutHistoryField = useCallback((value: string): string | null => {
    if (!draft) return null;
    if (draft.trading.accountType !== 'prop-firm') return null;
    if (draft.trading.propFirmStatus !== 'funded') return null;
    if (!value) return 'Payout history is required for funded accounts';
    return null;
  }, [draft]);

  const validateBrokerField = (value: string): string | null => {
    const result = validateBroker(value);
    return result.isValid ? null : result.error || null;
  };

  // Real-time validation - only show errors after field is touched
  const primaryFocusError = useMemo(() => {
    if (!primaryFocusTouched || !draft) return null;
    return validatePrimaryFocusField(draft.trading.primaryFocus);
  }, [primaryFocusTouched, draft]);

  const accountTypeError = useMemo(() => {
    if (!accountTypeTouched || !draft) return null;
    return validateAccountTypeField(draft.trading.accountType);
  }, [accountTypeTouched, draft]);

  const propFirmError = useMemo(() => {
    if (!propFirmTouched || !draft) return null;
    return validatePropFirmField(draft.trading.propFirm);
  }, [propFirmTouched, draft, validatePropFirmField]);

  const propFirmStatusError = useMemo(() => {
    if (!propFirmStatusTouched || !draft) return null;
    return validatePropFirmStatusField(draft.trading.propFirmStatus);
  }, [propFirmStatusTouched, draft, validatePropFirmStatusField]);

  const propFirmPayoutHistoryError = useMemo(() => {
    if (!propFirmPayoutHistoryTouched || !draft) return null;
    return validatePropFirmPayoutHistoryField(draft.trading.propFirmPayoutHistory);
  }, [propFirmPayoutHistoryTouched, draft, validatePropFirmPayoutHistoryField]);

  const brokerError = useMemo(() => {
    if (!brokerTouched || !draft) return null;
    return validateBrokerField(draft.trading.broker);
  }, [brokerTouched, draft]);

  // Update step completion status when validation changes
  useEffect(() => {
    if (!draft) return;

    const isStepValid = validateTradingStep(draft).isValid;
    const isCompleted = draft.completedSteps.includes('trading');

    if (isStepValid && !isCompleted) {
      updateData((current) => ({
        ...current,
        completedSteps: [...current.completedSteps, 'trading']
      }));
    } else if (!isStepValid && isCompleted) {
      updateData((current) => ({
        ...current,
        completedSteps: current.completedSteps.filter(step => step !== 'trading')
      }));
    }
  }, [draft, updateData]);

  // Handle field changes
  const handlePrimaryFocusChange = (values: TradingFocus[]): void => {
    updateData((current) => ({
      ...current,
      trading: {
        ...current.trading,
        primaryFocus: values
      },
      lastModified: new Date().toISOString()
    }));
  };

  const handleAccountTypeChange = (value: BetaAccountType): void => {
    // Clear prop firm fields when switching away from prop-firm
    if (value !== 'prop-firm') {
      setPropFirmTouched(false);
      setPropFirmStatusTouched(false);
      setPropFirmPayoutHistoryTouched(false);
    }

    updateData((current) => ({
      ...current,
      trading: {
        ...current.trading,
        accountType: value,
        // Clear prop firm fields when switching away from prop-firm
        propFirm: value !== 'prop-firm' ? '' : current.trading.propFirm,
        propFirmStatus: value !== 'prop-firm' ? '' : current.trading.propFirmStatus,
        propFirmPayoutHistory: value !== 'prop-firm' ? '' : current.trading.propFirmPayoutHistory
      },
      lastModified: new Date().toISOString()
    }));
  };

  const handlePropFirmChange = (value: string): void => {
    updateData((current) => ({
      ...current,
      trading: {
        ...current.trading,
        propFirm: value
      },
      lastModified: new Date().toISOString()
    }));
  };

  const handlePropFirmStatusChange = (value: PropFirmStatus): void => {
    // Clear payout history when switching away from funded
    if (value !== 'funded') {
      setPropFirmPayoutHistoryTouched(false);
    }

    updateData((current) => ({
      ...current,
      trading: {
        ...current.trading,
        propFirmStatus: value,
        // Clear payout history when switching away from funded
        propFirmPayoutHistory: value !== 'funded' ? '' : current.trading.propFirmPayoutHistory
      },
      lastModified: new Date().toISOString()
    }));
  };

  const handlePropFirmPayoutHistoryChange = (value: PropFirmPayoutHistory): void => {
    updateData((current) => ({
      ...current,
      trading: {
        ...current.trading,
        propFirmPayoutHistory: value
      },
      lastModified: new Date().toISOString()
    }));
  };

  const handleBrokerChange = (value: string): void => {
    updateData((current) => ({
      ...current,
      trading: {
        ...current.trading,
        broker: value
      },
      lastModified: new Date().toISOString()
    }));
  };

  if (!draft) {
    return <div>Loading...</div>;
  }

  const showPropFirmFields = draft.trading.accountType === 'prop-firm';
  const showPayoutHistory = showPropFirmFields && draft.trading.propFirmStatus === 'funded';

  return (
      <Card
      radius='none'
      shadow='none'
      className="max-w-2xl mx-auto"
      >
        <CardHeader>
          <div className="text-2xl font-semibold mb-2">
            <h2 >Trading Basics</h2>
          </div>
        </CardHeader>

        <CardBody>
          <Form className="space-y-6">
            {/* Primary Trading Focus */}
            <Select
              label="Primary Asset(s) Traded"
              labelPlacement="outside"
              placeholder="Select your primary asset class"
              description="What type of financial instruments do you primarily trade?"
              selectionMode='multiple'
              selectedKeys={draft.trading.primaryFocus}
              onSelectionChange={(keys) => {
                const values = Array.from(keys) as TradingFocus[];
                setPrimaryFocusTouched(true);
                handlePrimaryFocusChange(values);
              }}
              isInvalid={!!primaryFocusError}
              errorMessage={primaryFocusError}
              // classNames={{
              //             listboxWrapper: "data-[hover=true]:bg-primary-50\
              //             data-[selectable=true]:focus:bg-primary-50\
              //               [&_[data-selected=true]]:!bg-primary-50\
              //               [&_[aria-selected=true]]:!bg-primary-50\
              //                 [&_[data-selected=false]]:!bg-transparent\
              //                 [&_[aria-selected=false]]:!bg-transparent",
              //             base: "text-base"
              //           }}
              isRequired
              isClearable
            >
              <SelectItem key={TradingFocusEnum.Futures}>
                Futures
              </SelectItem>
              <SelectItem key={TradingFocusEnum.Forex}>
                Forex
              </SelectItem>
              <SelectItem key={TradingFocusEnum.Crypto}>
                Cryptocurrency
              </SelectItem>
              <SelectItem key={TradingFocusEnum.Stocks}>
                Stocks & ETFs
              </SelectItem>
              <SelectItem key={TradingFocusEnum.Options}>
                Options
              </SelectItem>
              <SelectItem key={TradingFocusEnum.Other}>
                Other
              </SelectItem>
            </Select>

            {/* Account Type */}
            <Select
              label="Account Type"
              labelPlacement="outside"
              placeholder="Select your account type"
              description="What type of trading account do you use?"
              selectedKeys={draft.trading.accountType ? [draft.trading.accountType] : []}
              onSelectionChange={(keys) => {
                const value = Array.from(keys)[0] as BetaAccountType;
                setAccountTypeTouched(true);
                handleAccountTypeChange(value);
              }}
              isInvalid={!!accountTypeError}
              errorMessage={accountTypeError}
              className="text-base"
              isRequired
            >
              <SelectItem key={BetaAccountTypeEnum.Personal}>
                Personal Trading Account
              </SelectItem>
              <SelectItem key={BetaAccountTypeEnum.PropFirm}>
                Prop Firm Account
              </SelectItem>
              <SelectItem key={BetaAccountTypeEnum.Paper}>
                Paper Trading Account
              </SelectItem>
              <SelectItem key={BetaAccountTypeEnum.Retirement}>
                Retirement Account
              </SelectItem>
              <SelectItem key={BetaAccountTypeEnum.Business}>
                Business Account
              </SelectItem>
              <SelectItem key={BetaAccountTypeEnum.Trust}>
                Trust Account
              </SelectItem>
              <SelectItem key={BetaAccountTypeEnum.Pooled}>
                Pooled Account
              </SelectItem>
              <SelectItem key={BetaAccountTypeEnum.Other}>
                Other
              </SelectItem>
            </Select>

            {/* Conditional Prop Firm Section */}
            {showPropFirmFields && (
              <Card
                radius='none'
                shadow='none'
                className="w-full border-t border-b border-default-200 border-dashed"
              >
                <CardBody className="space-y-6 pt-6">

                  {/* Prop Firm Name */}
                  <Input
                    label="Prop Firm Name"
                    labelPlacement='outside-top'
                    placeholder="Enter your prop firm name"
                    description="Which prop firm are you trading with?"
                    value={draft.trading.propFirm}
                    onValueChange={handlePropFirmChange}
                    onBlur={() => setPropFirmTouched(true)}
                    isInvalid={!!propFirmError}
                    errorMessage={propFirmError}
                    classNames={{
                      input: "text-base", // 16px font size to prevent mobile zoom
                    }}
                    isRequired
                  />

                  {/* Prop Firm Status */}
                  <Select
                    label="Prop Firm Status"
                    labelPlacement='outside'
                    placeholder="Select your current status"
                    description="Are you in evaluation phase or funded?"
                    selectedKeys={draft.trading.propFirmStatus ? [draft.trading.propFirmStatus] : []}
                    onSelectionChange={(keys) => {
                      const value = Array.from(keys)[0] as PropFirmStatus;
                      setPropFirmStatusTouched(true);
                      handlePropFirmStatusChange(value);
                    }}
                    isInvalid={!!propFirmStatusError}
                    errorMessage={propFirmStatusError}
                    className="text-base"
                    isRequired
                  >
                    <SelectItem key={PropFirmStatusEnum.Evaluation}>
                      Evaluation Phase
                    </SelectItem>
                    <SelectItem key={PropFirmStatusEnum.Funded}>
                      Funded Trader
                    </SelectItem>
                  </Select>

                  {/* Conditional Payout History */}
                  {showPayoutHistory && (
                    <Select
                      label="Payout History"
                      labelPlacement='outside'
                      placeholder="Select your payout status"
                      description="Have you received any payouts from your prop firm?"
                      selectedKeys={draft.trading.propFirmPayoutHistory ? [draft.trading.propFirmPayoutHistory] : []}
                      onSelectionChange={(keys) => {
                        const value = Array.from(keys)[0] as PropFirmPayoutHistory;
                        setPropFirmPayoutHistoryTouched(true);
                        handlePropFirmPayoutHistoryChange(value);
                      }}
                      isInvalid={!!propFirmPayoutHistoryError}
                      errorMessage={propFirmPayoutHistoryError}
                      className="text-base"
                      isRequired
                    >
                      <SelectItem key={PropFirmPayoutHistoryEnum.YesReceived}>
                        Yes, I have received payouts
                      </SelectItem>
                      <SelectItem key={PropFirmPayoutHistoryEnum.NoNone}>
                        No, I have not received payouts
                      </SelectItem>
                      <SelectItem key={PropFirmPayoutHistoryEnum.PreferNotToSay}>
                        I prefer not to say
                      </SelectItem>
                    </Select>
                  )}
                </CardBody>
              </Card>
            )}

            {/* Broker */}
            <Input
              label="Broker"
              labelPlacement="outside"
              placeholder="Enter your broker name"
              description="Which broker do you use for trading?"
              value={draft.trading.broker}
              onValueChange={handleBrokerChange}
              onBlur={() => setBrokerTouched(true)}
              isInvalid={!!brokerError}
              errorMessage={brokerError}
              classNames={{
                input: "text-base", // 16px font size to prevent mobile zoom
              }}
              isRequired
            />
          </Form>
        </CardBody>
      </Card>
  );
};